<template>
  <button
    type="button"
    class="btn btn-sm btn-outline-primary flex-shrink-0"
    @click="openChart"
  >
    <font-awesome-icon icon="chart-line" class="me-1" />
    {{ $t("dashboard.issuance_chart.title") }}
  </button>

  <ModalComponent
    ref="chartModal"
    :submitButtonText="$t('common.close')"
    :noFooter="true"
    size="xl"
  >
    <template #title>
      {{ $t("dashboard.issuance_chart.title") }}
    </template>
    <template #body>
      <div class="pb-3">
        <div
          class="d-flex justify-content-between align-items-start gap-3 flex-wrap"
        >
          <p class="text-muted fs-9 mb-2">
            {{ $t("dashboard.issuance_chart.description") }}
          </p>
          <div
            v-if="hasChart"
            class="btn-group btn-group-sm flex-shrink-0"
            role="group"
          >
            <button
              type="button"
              class="btn"
              :class="isLogScale ? 'btn-outline-secondary' : 'btn-secondary'"
              @click="isLogScale = false"
            >
              {{ $t("dashboard.issuance_chart.scale_absolute") }}
            </button>
            <button
              type="button"
              class="btn"
              :class="isLogScale ? 'btn-secondary' : 'btn-outline-secondary'"
              @click="isLogScale = true"
            >
              {{ $t("dashboard.issuance_chart.scale_log") }}
            </button>
          </div>
        </div>

        <LoadingIndicatorComponent v-if="isLoading" />
        <div v-else-if="hasFailed" class="alert alert-warning mb-0">
          {{ $t("dashboard.issuance_chart.load_failed") }}
        </div>
        <div
          v-else-if="!chartData.datasets.length"
          class="text-center text-muted py-5"
        >
          {{ $t("dashboard.issuance_chart.no_data") }}
        </div>
        <template v-else>
          <LineChart
            :chartData="chartData"
            :yAxisTitle="yAxisTitle"
            :height="420"
            :showLegend="showLegend"
            :tooltipLabel="tooltipLabel"
            :yScaleType="isLogScale ? 'logarithmic' : 'linear'"
            :yMin="isLogScale ? zeroFloor : undefined"
            interactionMode="nearest"
          />
          <p v-if="isLogScale" class="text-muted fs-9 mb-0">
            {{ $t("dashboard.issuance_chart.zero_marker_note") }}
          </p>
        </template>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import LineChart from "@/common/LineChart.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import ModalComponent from "@/components/common/ModalComponent.vue";
import { PALETTE, getChartColor } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { LineDataset } from "@/types/ChartData";
import type { TooltipItem } from "chart.js";
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import {
  IssuanceChartDevice,
  IssuanceChartMonth,
  issuanceChartApi,
} from "./issuanceChartApi";

const props = defineProps<{
  companyIds: number[];
}>();

const { t } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const chartModal =
  useTemplateRef<InstanceType<typeof ModalComponent>>("chartModal");
const months = ref<IssuanceChartMonth[]>([]);
const devices = ref<IssuanceChartDevice[]>([]);
const isLoading = ref(false);
const hasFailed = ref(false);
const isLogScale = ref(false);

// Only the consolidated view spans several companies, and only there does the device name
// alone leave the reader guessing whose device they are looking at.
const showCompany = computed(() => props.companyIds.length > 1);

// Past the categorical palette's stops the colours are generated and a legend stops being
// readable at a glance; the tooltip carries the identity from there on.
const showLegend = computed(
  () => devices.value.length <= PALETTE.categorical.length
);

// Keeps the scale toggle out of the way while a refetch is in flight or has failed, even
// though the previous fetch's series are still in hand.
const hasChart = computed(
  () => !isLoading.value && !hasFailed.value && devices.value.length > 0
);

const yAxisTitle = computed(() =>
  isLogScale.value
    ? t("dashboard.issuance_chart.y_axis_log")
    : t("dashboard.issuance_chart.y_axis")
);

/**
 * Where a zero-issuance month is drawn on the log axis: one decade below the smallest real
 * value, so the floor line is reserved for zeros and no genuine datapoint lands on it.
 *
 * A log axis cannot express 0, and Chart.js turns a zero into `undefined` while parsing, which
 * the line controller then skips exactly like a null. That would make "this device issued
 * nothing in March" indistinguishable from "this device did not exist in March" — the single
 * most important reading of this chart. So zeros are substituted with this floor and marked
 * with their own point style instead.
 */
const zeroFloor = computed(() => {
  const positives = devices.value.flatMap((device) =>
    device.issued_mwh.filter((value): value is number => !!value && value > 0)
  );
  const smallest = positives.length ? Math.min(...positives) : 1;
  return 10 ** (Math.floor(Math.log10(smallest)) - 1);
});

const chartData = computed<{ labels: string[]; datasets: LineDataset[] }>(
  () => ({
    labels: months.value.map(monthLabel),
    datasets: devices.value.map((device, index) => {
      const color = getChartColor({ index });
      const isZero = device.issued_mwh.map((value) => value === 0);
      return {
        label: device.name,
        data: isLogScale.value
          ? device.issued_mwh.map((value) =>
              value === 0 ? zeroFloor.value : value
            )
          : device.issued_mwh,
        borderColor: color,
        backgroundColor: color,
        // Bezier smoothing interpolates in pixel space, so on a log axis it can sag a curve
        // visibly below the floor between a real value and a substituted zero.
        tension: isLogScale.value ? 0 : 0.2,
        fill: false,
        pointRadius: isZero.map((zero) => (zero && isLogScale.value ? 4 : 2)),
        pointStyle: isZero.map((zero) =>
          zero && isLogScale.value ? "crossRot" : "circle"
        ),
        pointHoverRadius: 5,
        borderWidth: 2,
      };
    }),
  })
);

const openChart = async () => {
  chartModal.value?.toggleModal();
  await fetchIssuance();
};

const fetchIssuance = async () => {
  isLoading.value = true;
  hasFailed.value = false;
  try {
    const response = await issuanceChartApi.getMonthlyDeviceIssuance(
      props.companyIds
    );
    months.value = response.data.months;
    devices.value = response.data.devices;
  } catch (error) {
    hasFailed.value = true;
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const monthLabel = (month: IssuanceChartMonth) =>
  `${month.year}-${String(month.month).padStart(2, "0")}`;

const tooltipLabel = (context: TooltipItem<"line">) => {
  const device = devices.value[context.datasetIndex];
  // Read the issued MWh back from the response rather than from the plotted point, so a
  // zero substituted by the log-scale floor still reports itself as 0.
  const issued = device?.issued_mwh[context.dataIndex] ?? 0;
  const mwh = issued.toLocaleString(undefined, { maximumFractionDigits: 1 });
  const lines = [`${device?.name ?? ""}: ${mwh} ${t("common.mwh")}`];
  if (showCompany.value && device?.company_name) {
    lines.push(device.company_name);
  }
  return lines;
};
</script>
